import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./PupEmergencyPlanPage.css";
import { ArrowLeft } from "lucide-react";
import nextIcon from "../../assets/nextButtonIcon2.png";
import TopNavBar from "../../components/TopNavBar";
import { useGlobalAlert } from "../../context/AlertContext";

import dogLevel1 from "../../assets/dogLevel1.png";
import dogLevel3 from "../../assets/dogLevel3.png";
import appIcon from "../../assets/appIcon.svg";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";

const PupEmergencyPlanPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const [isValid, setIsValid] = useState(false);

  const [vetDetails, setVetDetails] = useState({
    vet_name: formData?.vet_name || "",
    vet_phone: formData?.vet_phone || "",
    vet_address: formData?.vet_address || "",
  });

  const [medicalInfo, setMedicalInfo] = useState({
    medical_conditions: formData?.medical_conditions || "",
    medications_and_supplements: formData?.medications_and_supplements || "",
  });

  const [backupContact, setBackupContact] = useState({
    backup_name: formData?.backup_name || "",
    backup_phone: formData?.backup_phone || "",
  });

  const [insurance, setInsurance] = useState({
    insurance_provider: formData?.insurance_provider || "",
    insurance_phone: formData?.insurance_phone || "",
    insurance_policy: formData?.insurance_policy || "",
  });

  const VALID_VACCINES = ["rabies", "lepto", "bordatella"];

  const initialVaccines = Array.isArray(formData?.vaccinations)
    ? formData.vaccinations
        .map((v) => v.toLowerCase())
        .filter((v) => VALID_VACCINES.includes(v))
    : [formData?.vaccine1, formData?.vaccine2, formData?.vaccine3]
        .filter(Boolean)
        .map((v) => v.toLowerCase())
        .filter((v) => VALID_VACCINES.includes(v));

  const [selectedVaccines, setSelectedVaccines] = useState(initialVaccines);

  const createHandler = (setter) => (e) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleVaccine = (vaccine) => {
    const lowerVax = vaccine.toLowerCase();
    setSelectedVaccines((prev) =>
      prev.includes(lowerVax)
        ? prev.filter((v) => v !== lowerVax)
        : [...prev, lowerVax],
    );
  };

  useEffect(() => {
    const required = [
      formData.vet_name,
      formData.vet_phone,
      formData.medical_conditions,
      formData.insurance_provider,
    ];
    setIsValid(required.every((field) => field && field.trim() !== ""));
  }, [formData]);

  const isValidPhoneNumber = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const handleNext = () => {
    if (!isValid) return;

    if (!isValidPhoneNumber(formData.vet_phone)) {
      triggerAlert("Enter valid phone number for Vet's Phone", "error");
      return;
    }

    if (formData.backup_phone && !isValidPhoneNumber(formData.backup_phone)) {
      triggerAlert("Enter valid phone number for backup contact", "error");
      return;
    }

    if (
      formData.insurance_phone &&
      !isValidPhoneNumber(formData.insurance_phone)
    ) {
      triggerAlert("Enter valid phone number for insurance", "error");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      vaccinations: selectedVaccines,
    }));

    navigate("/house-training");
  };

  return (
    <div className="pepe-container">
      <header className="pepe-desktop-header pepe-desktop-only">
        <button className="pepe-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "#000" }}>Back</span>
        </button>
        <TopNavBar theme="light" />
      </header>

      {/* Mobile Top Header (Hidden on Desktop) */}
      {/* <div className="pepe-mobile-header pepe-mobile-only">
        <button
          className="pepe-back-btn pepe-mobile-back-override"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={22} color="#000" strokeWidth={3} />
          <span style={{ color: "#000" }}>Back</span>
        </button>
        <div className="pepe-header-logo" style={{ color: "#009fd9" }}>
          <img src={appIcon} alt="Social Paws" className="pepe-logo-img" />
          <span>Social Paws</span>
        </div>
      </div> */}
      <div
        className="cb-custom-top-header cb-mobile-only"
        style={{ background: "transparent" }}
      >
        {/* TOP ROW */}
        <div className="cb-header-top-row">
          <MobileTopBar
            onBack={() => navigate("/walkies-and-food", { state: { returnToStep: 2 } })}
            backColor="#000"
            logoColor="#009fd9"
            blueLogo={true}
          />
        </div>

        {/* BOTTOM ROW */}
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <div className="pepe-content">
        <h1 className="pepe-title">PUP-EMERGENCY PLAN</h1>

        <div className="pepe-grid-layout">
          {/* Left Column Data */}
          <div className="pepe-column">
            <div className="pepe-section">
              <label className="pepe-label">PRIMARY VET</label>
              <input
                type="text"
                name="vet_name"
                placeholder="Vet's Name/Clinic"
                className="pepe-input"
                value={formData.vet_name || ""}
                onChange={createHandler(setVetDetails)}
              />
              <input
                type="tel"
                name="vet_phone"
                placeholder="Vet's Phone"
                className="pepe-input"
                value={formData.vet_phone || ""}
                onChange={createHandler(setVetDetails)}
              />
              <input
                type="text"
                name="vet_address"
                placeholder="Vet's Address"
                className="pepe-input"
                value={formData.vet_address || ""}
                onChange={createHandler(setVetDetails)}
              />
            </div>

            <div className="pepe-section">
              <label className="pepe-label">MEDICAL CONDITIONS</label>
              <textarea
                name="medical_conditions"
                placeholder="Hip Dysplasia, anxiety, seizures..."
                className="pepe-textarea"
                value={formData.medical_conditions || ""}
                onChange={createHandler(setMedicalInfo)}
              />
            </div>

            <div className="pepe-section">
              <label className="pepe-label">MEDICATIONS AND SUPPLEMENTS</label>
              <textarea
                name="medications_and_supplements"
                placeholder="Medication name, dosage, schedule"
                className="pepe-textarea"
                value={formData.medications_and_supplements || ""}
                onChange={createHandler(setMedicalInfo)}
              />
            </div>
          </div>

          {/* Right Column Data */}
          <div className="pepe-column">
            <div className="pepe-section">
              <label className="pepe-label">BACKUP CONTACT</label>
              <label className="pepe-sub">
                Optional: In case you are not accessible
              </label>
              <input
                type="text"
                name="backup_name"
                placeholder="Your Name"
                className="pepe-input"
                value={formData.backup_name || ""}
                onChange={createHandler(setBackupContact)}
              />
              <input
                type="tel"
                name="backup_phone"
                placeholder="Your Number"
                className="pepe-input"
                value={formData.backup_phone || ""}
                onChange={createHandler(setBackupContact)}
              />
            </div>

            <div className="pepe-section">
              <label className="pepe-label">INSURANCE</label>
              <input
                type="text"
                name="insurance_provider"
                placeholder="Insurance Provider"
                className="pepe-input"
                value={formData.insurance_provider || ""}
                onChange={createHandler(setInsurance)}
              />
              <input
                type="tel"
                name="insurance_phone"
                placeholder="Phone #"
                className="pepe-input"
                value={formData.insurance_phone || ""}
                onChange={createHandler(setInsurance)}
              />
              <input
                type="text"
                name="insurance_policy"
                placeholder="Policy #"
                className="pepe-input"
                value={formData.insurance_policy || ""}
                onChange={createHandler(setInsurance)}
              />
            </div>

            <div className="pepe-section">
              <label className="pepe-label">VACCINATIONS</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                  marginTop: "1rem",
                }}
              >
                {["Rabies", "Lepto", "Bordatella"].map((vax) => {
                  const isActive = selectedVaccines.includes(vax.toLowerCase());
                  return (
                    <div
                      key={vax}
                      className="pepe-toggle-group-ht"
                      style={{ justifyContent: "space-between", width: "100%" }}
                    >
                      <span
                        className="pep-toggle-label-text-ht"
                        style={{ fontSize: "1rem" }}
                      >
                        {vax.toUpperCase()}
                      </span>
                      <div
                        className={`pepe-dog-toggle-switch-ht ${isActive ? "on" : "off"}`}
                        onClick={() => toggleVaccine(vax)}
                      >
                        <div className="pepe-toggle-track-ht" />
                        <div className="pepe-toggle-thumb-ht">
                          <img
                            src={isActive ? dogLevel3 : dogLevel1}
                            alt="Toggle"
                            style={{ width: isActive ? "75px" : "65px" }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next Button Footer Area */}
            <div
              className={`pepe-next-btn-container ${!isValid ? "disabled" : ""} desktop-only`}
              onClick={handleNext}
            >
              <span className="pepe-next-text">NEXT</span>
              <div className="pepe-next-icon">
                <img src={nextIcon} alt="Next" />
              </div>
            </div>
            <MobileBottomBar
              onNext={handleNext}
              isNextEnabled={isValid}
              nextIconSrc={nextIcon}
              variant="blue"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PupEmergencyPlanPage;
